// Homepage section types and layout variants supported by the school-sites SectionRenderer.
// Keep in sync with school-sites/components/SectionRenderer.vue (canonical variant names only —
// legacy aliases like Carousel/Counters/IconGrid/Banner/Strip/Accordion are excluded).

export const THEME_OPTIONS = [
  { text: 'Default', value: 'default' },
  { text: 'Modern', value: 'modern' },
  { text: 'Bold', value: 'bold' },
];

export const SECTION_VARIANTS = {
  Hero: [
    'Default',
    'Split',
    'BgImage',
    'TextOnly',
    'Fullwidth',
    'Fullwidth2',
    'SplitBullets',
    'Stacked',
    'Diagonal',
    'ImageTop',
    'Form',
    'FormSplit',
    'FeatureCards',
    'Floating',
    'Video',
  ],
  Programs: ['Default', 'Minimal', 'Showcase', 'Horizontal', 'Carousel', 'Spotlight', 'Pricing', 'Tiles', 'Immersive'],
  Stats: ['Default', 'TrustBar', 'Cards', 'Split', 'Inline', 'Bold'],
  Features: ['Default', 'Alternating', 'Timeline', 'Split', 'Cards', 'Numbered'],
  Classes: ['Default', 'List', 'Timeline', 'Calendar', 'Cards', 'Table'],
  Expertise: ['Default', 'Split', 'ImageBg', 'Cards', 'Stats', 'Quote'],
  Testimonials: ['Default', 'Grid', 'Marquee', 'Spotlight', 'Cards', 'Stacked'],
  CTA: ['Default', 'Centered', 'GradientWave', 'Split', 'Image', 'Minimal'],
  FAQ: ['Default', 'TwoColumn', 'Cards', 'Split', 'Boxed', 'Simple'],
  Partners: ['Default', 'Grid', 'Marquee', 'Cards', 'Compact', 'Bordered'],
  AuthorizingBody: ['Default', 'Grid', 'Marquee', 'Cards', 'Compact', 'Bordered'],
};

export const SECTION_TYPES = Object.keys(SECTION_VARIANTS);

// Header / navbar variants offered in Basic Settings. Keep in sync with
// school-sites/utils/headerVariants.ts (HEADER_VARIANTS) and the Header/* components.
export const HEADER_VARIANTS = [
  'Default',
  'Centered',
  'Minimal',
  'Bold',
  'Pill',
  'Contact',
  'Gradient',
  'Glass',
  'Search',
  'Drawer',
  'Utility',
  'Split',
];

export const HEADER_VARIANT_LABELS = {
  Default: 'Default',
  Centered: 'Centered',
  Minimal: 'Minimal',
  Bold: 'Bold Bar',
  Pill: 'Pill Nav',
  Contact: 'Contact',
  Gradient: 'Gradient',
  Glass: 'Glass',
  Search: 'With Search',
  Drawer: 'Drawer',
  Utility: 'Utility Bar',
  Split: 'Split Nav',
};

// Per-variant schematic used to render a distinct SVG thumbnail (below).
// topbar: thin strip above the bar; barFill: main bar background;
// logo: 'left' | 'center'; nav: 'right' | 'center' | 'below' | 'split' | 'menu';
// cta: show a pill CTA; pill/search/floating tweak the nav chrome.
const HEADER_THUMB_SPECS = {
  Default: { topbar: '#111827', logo: 'left', nav: 'right', cta: true },
  Centered: { accent: '#2563eb', logo: 'center', nav: 'below', cta: true },
  Minimal: { logo: 'left', nav: 'right', cta: false },
  Bold: { barFill: '#2563eb', dark: true, logo: 'left', nav: 'right', cta: true },
  Pill: { logo: 'left', nav: 'center', pill: true, cta: true },
  Contact: { topbar: '#1e3a8a', logo: 'left', nav: 'center', cta: true },
  Gradient: { gradient: true, dark: true, logo: 'left', nav: 'right', cta: true },
  Glass: { wash: true, floating: true, logo: 'left', nav: 'center', cta: true },
  Search: { logo: 'left', nav: 'center', search: true, cta: true },
  Drawer: { logo: 'center', nav: 'menu', cta: true },
  Utility: { topbar: '#111827', social: true, logo: 'left', nav: 'right', cta: true },
  Split: { logo: 'center', nav: 'split', cta: true },
};

function buildHeaderThumbSvg(spec) {
  const W = 400;
  const H = 130;
  const PRIMARY = '#2563eb';
  const INK = spec.dark ? '#ffffff' : '#334155';
  const parts = [];

  // Backdrop
  const wash = spec.wash
    ? `<rect width="${W}" height="${H}" fill="#eef2ff"/>`
    : `<rect width="${W}" height="${H}" fill="#f8fafc"/>`;
  parts.push(wash);

  let barY = 0;
  const barH = spec.nav === 'below' ? 84 : 72;

  // Topbar strip
  if (spec.topbar) {
    parts.push(`<rect x="0" y="0" width="${W}" height="22" fill="${spec.topbar}"/>`);
    parts.push(`<rect x="20" y="8" width="70" height="6" rx="3" fill="#94a3b8"/>`);
    if (spec.social) {
      parts.push(`<circle cx="345" cy="11" r="4" fill="#94a3b8"/><circle cx="360" cy="11" r="4" fill="#94a3b8"/><circle cx="375" cy="11" r="4" fill="#94a3b8"/>`);
    } else {
      parts.push(`<rect x="320" y="6" width="60" height="11" rx="5" fill="${PRIMARY}"/>`);
    }
    barY = 22;
  }
  if (spec.accent) {
    parts.push(`<rect x="0" y="0" width="${W}" height="5" fill="${spec.accent}"/>`);
    barY = 5;
  }

  // Main bar background
  const barTop = barY + (spec.floating ? 14 : 0);
  const barLeft = spec.floating ? 18 : 0;
  const barWidth = spec.floating ? W - 36 : W;
  const barRadius = spec.floating || spec.pill ? 14 : 0;
  let barFill = spec.barFill || '#ffffff';
  if (spec.gradient) {
    parts.push(`<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${PRIMARY}"/><stop offset="1" stop-color="#7c3aed"/></linearGradient></defs>`);
    barFill = 'url(#g)';
  }
  if (spec.floating) {
    parts.push(`<rect x="${barLeft}" y="${barTop}" width="${barWidth}" height="${barH - 20}" rx="${barRadius}" fill="rgba(255,255,255,0.7)" stroke="#e2e8f0"/>`);
  } else {
    parts.push(`<rect x="${barLeft}" y="${barTop}" width="${barWidth}" height="${barH}" rx="${barRadius}" fill="${barFill}"/>`);
    if (!spec.barFill && !spec.gradient) {
      parts.push(`<rect x="${barLeft}" y="${barTop + barH - 1}" width="${barWidth}" height="1" fill="#e2e8f0"/>`);
    }
  }

  const midY = barTop + (spec.floating ? (barH - 20) / 2 : barH / 2);

  // Logo
  const logoX = spec.logo === 'center' ? W / 2 - 24 : barLeft + 18;
  parts.push(`<rect x="${logoX}" y="${midY - 8}" width="48" height="16" rx="4" fill="${spec.dark ? 'rgba(255,255,255,0.85)' : PRIMARY}"/>`);

  // Drawer menu button
  if (spec.nav === 'menu') {
    parts.push(`<rect x="${barLeft + 18}" y="${midY - 6}" width="18" height="3" rx="1.5" fill="${INK}"/>`);
    parts.push(`<rect x="${barLeft + 18}" y="${midY - 1}" width="18" height="3" rx="1.5" fill="${INK}"/>`);
    parts.push(`<rect x="${barLeft + 18}" y="${midY + 4}" width="18" height="3" rx="1.5" fill="${INK}"/>`);
  }

  // Nav links
  const link = (x, y) => `<rect x="${x}" y="${y - 3}" width="34" height="6" rx="3" fill="${spec.dark ? 'rgba(255,255,255,0.75)' : '#94a3b8'}"/>`;
  const navColor = spec.dark ? 'rgba(255,255,255,0.75)' : '#94a3b8';
  if (spec.nav === 'below') {
    const startX = W / 2 - 90;
    for (let i = 0; i < 4; i++) parts.push(link(startX + i * 46, barTop + barH - 14));
  } else if (spec.nav === 'center') {
    const navW = 4 * 44;
    const startX = W / 2 - navW / 2;
    if (spec.pill) parts.push(`<rect x="${startX - 12}" y="${midY - 14}" width="${navW + 8}" height="28" rx="14" fill="#eef2ff"/>`);
    for (let i = 0; i < 4; i++) parts.push(`<rect x="${startX + i * 44}" y="${midY - 3}" width="30" height="6" rx="3" fill="${spec.pill ? navColor : navColor}"/>`);
  } else if (spec.nav === 'right') {
    let startX = W - (spec.cta ? 210 : 160);
    if (spec.floating) startX = barLeft + barWidth - 220;
    for (let i = 0; i < 3; i++) parts.push(link(startX + i * 44, midY));
  } else if (spec.nav === 'split') {
    for (let i = 0; i < 2; i++) parts.push(link(barLeft + 90 + i * 44, midY));
    for (let i = 0; i < 2; i++) parts.push(link(W - 210 + i * 44, midY));
  }

  // Search box
  if (spec.search) {
    parts.push(`<rect x="${W - 190}" y="${midY - 11}" width="80" height="22" rx="11" fill="#eef2ff" stroke="#e2e8f0"/>`);
    parts.push(`<circle cx="${W - 178}" cy="${midY}" r="4" fill="none" stroke="#94a3b8" stroke-width="2"/>`);
  }

  // CTA pill
  if (spec.cta) {
    const ctaX = barLeft + barWidth - (spec.floating ? 92 : 90);
    const ctaFill = spec.dark ? '#ffffff' : PRIMARY;
    parts.push(`<rect x="${ctaX}" y="${midY - 12}" width="72" height="24" rx="12" fill="${ctaFill}"/>`);
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
}

// Returns a self-contained SVG data URI thumbnail for a header variant.
export function headerVariantImage(variant) {
  const spec = HEADER_THUMB_SPECS[variant] || HEADER_THUMB_SPECS.Default;
  const svg = buildHeaderThumbSvg(spec);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Footer variants offered in Basic Settings. Keep in sync with
// school-sites/utils/footerVariants.ts (FOOTER_VARIANTS) and the Footer/* components.
export const FOOTER_VARIANTS = [
  'Default',
  'Columns',
  'Centered',
  'Minimal',
  'Cta',
  'Gradient',
  'Newsletter',
  'Contact',
];

export const FOOTER_VARIANT_LABELS = {
  Default: 'Default',
  Columns: 'Columns',
  Centered: 'Centered',
  Minimal: 'Minimal',
  Cta: 'CTA',
  Gradient: 'Gradient',
  Newsletter: 'Newsletter',
  Contact: 'Contact',
};

// Per-variant schematic used to render a distinct SVG thumbnail (below).
// cols: number of link columns; logo: 'left' | 'center'; social: show social dots;
// cta: leading call-to-action band; newsletter: email capture row;
// gradient: gradient backdrop; minimal: single slim bar; contact: contact info block.
const FOOTER_THUMB_SPECS = {
  Default: { cols: 3, logo: 'left', social: true },
  Columns: { cols: 4, logo: 'left', social: true },
  Centered: { cols: 0, logo: 'center', social: true, centered: true },
  Minimal: { cols: 0, logo: 'left', social: true, minimal: true },
  Cta: { cols: 3, logo: 'left', social: true, cta: true },
  Gradient: { cols: 3, logo: 'left', social: true, gradient: true },
  Newsletter: { cols: 2, logo: 'left', social: true, newsletter: true },
  Contact: { cols: 2, logo: 'left', social: true, contact: true },
};

function buildFooterThumbSvg(spec) {
  const W = 400;
  const H = 130;
  const PRIMARY = '#2563eb';
  const parts = [];

  // Dark footer backdrop (matches the live bg-gray-900 footer).
  if (spec.gradient) {
    parts.push(`<defs><linearGradient id="fg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${PRIMARY}"/><stop offset="1" stop-color="#7c3aed"/></linearGradient></defs>`);
    parts.push(`<rect width="${W}" height="${H}" fill="url(#fg)"/>`);
  } else {
    parts.push(`<rect width="${W}" height="${H}" fill="#111827"/>`);
  }

  const inkStrong = 'rgba(255,255,255,0.9)';
  const ink = 'rgba(255,255,255,0.45)';
  let topY = 16;

  // Leading CTA band
  if (spec.cta) {
    parts.push(`<rect x="0" y="0" width="${W}" height="34" fill="rgba(255,255,255,0.06)"/>`);
    parts.push(`<rect x="24" y="12" width="140" height="10" rx="5" fill="${inkStrong}"/>`);
    parts.push(`<rect x="300" y="9" width="76" height="16" rx="8" fill="${PRIMARY}"/>`);
    topY = 50;
  }

  // Newsletter capture row
  if (spec.newsletter) {
    parts.push(`<rect x="220" y="${topY}" width="120" height="16" rx="8" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)"/>`);
    parts.push(`<rect x="346" y="${topY}" width="30" height="16" rx="8" fill="${PRIMARY}"/>`);
  }

  // Minimal: single slim centered/left bar and done
  if (spec.minimal) {
    const my = H / 2;
    const lx = spec.logo === 'center' ? W / 2 - 24 : 24;
    parts.push(`<rect x="${lx}" y="${my - 7}" width="48" height="14" rx="4" fill="${inkStrong}"/>`);
    for (let i = 0; i < 3; i++) parts.push(`<rect x="${140 + i * 44}" y="${my - 3}" width="30" height="6" rx="3" fill="${ink}"/>`);
    parts.push(`<circle cx="${W - 60}" cy="${my}" r="5" fill="${ink}"/><circle cx="${W - 42}" cy="${my}" r="5" fill="${ink}"/><circle cx="${W - 24}" cy="${my}" r="5" fill="${ink}"/>`);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
  }

  // Centered: stacked logo, nav row, social dots
  if (spec.centered) {
    parts.push(`<rect x="${W / 2 - 28}" y="${topY}" width="56" height="16" rx="4" fill="${inkStrong}"/>`);
    const navW = 4 * 44;
    for (let i = 0; i < 4; i++) parts.push(`<rect x="${W / 2 - navW / 2 + i * 44}" y="${topY + 30}" width="30" height="6" rx="3" fill="${ink}"/>`);
    const sx = W / 2 - 27;
    for (let i = 0; i < 3; i++) parts.push(`<circle cx="${sx + i * 27}" cy="${topY + 58}" r="6" fill="rgba(255,255,255,0.2)"/>`);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
  }

  // Logo/brand column
  const logoX = 24;
  parts.push(`<rect x="${logoX}" y="${topY}" width="52" height="16" rx="4" fill="${inkStrong}"/>`);
  parts.push(`<rect x="${logoX}" y="${topY + 26}" width="110" height="5" rx="2.5" fill="${ink}"/>`);
  parts.push(`<rect x="${logoX}" y="${topY + 36}" width="90" height="5" rx="2.5" fill="${ink}"/>`);
  if (spec.social) {
    for (let i = 0; i < 3; i++) parts.push(`<circle cx="${logoX + 8 + i * 20}" cy="${topY + 58}" r="6" fill="rgba(255,255,255,0.2)"/>`);
  }

  // Contact block (single rich column) vs link columns
  if (spec.contact) {
    const cx = 200;
    parts.push(`<rect x="${cx}" y="${topY}" width="60" height="8" rx="4" fill="${inkStrong}"/>`);
    for (let i = 0; i < 3; i++) {
      parts.push(`<circle cx="${cx + 5}" cy="${topY + 24 + i * 18}" r="4" fill="${ink}"/>`);
      parts.push(`<rect x="${cx + 16}" y="${topY + 20 + i * 18}" width="120" height="6" rx="3" fill="${ink}"/>`);
    }
  } else {
    const cols = spec.cols || 3;
    const colGap = 62;
    const startX = W - 24 - (cols - 1) * colGap - 44;
    for (let c = 0; c < cols; c++) {
      const x = startX + c * colGap;
      parts.push(`<rect x="${x}" y="${topY}" width="40" height="8" rx="4" fill="${inkStrong}"/>`);
      for (let r = 0; r < 3; r++) {
        parts.push(`<rect x="${x}" y="${topY + 20 + r * 14}" width="34" height="5" rx="2.5" fill="${ink}"/>`);
      }
    }
  }

  // Bottom copyright rule
  parts.push(`<rect x="24" y="${H - 26}" width="${W - 48}" height="1" fill="rgba(255,255,255,0.12)"/>`);
  parts.push(`<rect x="${W / 2 - 55}" y="${H - 16}" width="110" height="5" rx="2.5" fill="rgba(255,255,255,0.3)"/>`);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
}

// Returns a self-contained SVG data URI thumbnail for a footer variant.
export function footerVariantImage(variant) {
  const spec = FOOTER_THUMB_SPECS[variant] || FOOTER_THUMB_SPECS.Default;
  const svg = buildFooterThumbSvg(spec);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Button corner shapes offered in Basic Settings. Applied site-wide to every CTA.
// Keep in sync with school-sites app.vue (--btn-radius mapping) and the DB button_style field.
export const BUTTON_STYLES = ['Rounded', 'Sharp', 'Pill'];

export const BUTTON_STYLE_LABELS = {
  Rounded: 'Rounded',
  Sharp: 'Sharp',
  Pill: 'Pill',
};

// Corner radius (px) each style renders in the picker thumbnail. Matches the
// --btn-radius values school-sites applies live (Sharp 0, Rounded ~8px, Pill full).
const BUTTON_THUMB_RADIUS = {
  Rounded: 8,
  Sharp: 0,
  Pill: 18,
};

// Returns a self-contained SVG data URI thumbnail: a primary button drawn with
// the given corner shape, so admins can preview the choice.
export function buttonStyleImage(style) {
  const W = 400;
  const H = 130;
  const PRIMARY = '#2563eb';
  const rx = BUTTON_THUMB_RADIUS[style] ?? BUTTON_THUMB_RADIUS.Rounded;
  const bw = 150;
  const bh = 44;
  const bx = (W - bw) / 2;
  const by = (H - bh) / 2;
  const parts = [
    `<rect width="${W}" height="${H}" fill="#f8fafc"/>`,
    `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="${rx}" fill="${PRIMARY}"/>`,
    `<rect x="${bx + 34}" y="${by + bh / 2 - 4}" width="${bw - 68}" height="8" rx="4" fill="rgba(255,255,255,0.9)"/>`,
  ];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Mirrors school-sites/themes/default.json — used to seed the section editor.
export const DEFAULT_THEME_SECTIONS = [
  { type: 'Hero', variant: 'Default' },
  { type: 'Partners', variant: 'Default' },
  { type: 'Programs', variant: 'Default' },
  { type: 'Stats', variant: 'Default' },
  { type: 'Features', variant: 'Default' },
  { type: 'Classes', variant: 'Default' },
  { type: 'Expertise', variant: 'Default' },
  { type: 'Testimonials', variant: 'Default' },
  { type: 'AuthorizingBody', variant: 'Default' },
  { type: 'CTA', variant: 'Default' },
];

// Mirrors school-sites/themes/*.json — the section order/variants each named theme renders
// when a school has no custom theme_sections saved.
export const THEME_DEFAULT_SECTIONS = {
  default: DEFAULT_THEME_SECTIONS,
  modern: [
    { type: 'Hero', variant: 'Split' },
    { type: 'Partners', variant: 'Default' },
    { type: 'Programs', variant: 'Minimal' },
    { type: 'Features', variant: 'Alternating' },
    { type: 'Stats', variant: 'Default' },
    { type: 'Testimonials', variant: 'Grid' },
    { type: 'Classes', variant: 'Default' },
    { type: 'AuthorizingBody', variant: 'Default' },
    { type: 'CTA', variant: 'Centered' },
  ],
  bold: [
    { type: 'Hero', variant: 'Fullwidth' },
    { type: 'Partners', variant: 'Default' },
    { type: 'Stats', variant: 'Default' },
    { type: 'Programs', variant: 'Default' },
    { type: 'Expertise', variant: 'Default' },
    { type: 'Features', variant: 'Default' },
    { type: 'Testimonials', variant: 'Default' },
    { type: 'Classes', variant: 'Default' },
    { type: 'AuthorizingBody', variant: 'Default' },
    { type: 'CTA', variant: 'Default' },
  ],
  hillendell: [
    { type: 'Hero', variant: 'Fullwidth2' },
    { type: 'Partners', variant: 'Default' },
    { type: 'Stats', variant: 'TrustBar' },
    { type: 'Programs', variant: 'Showcase' },
    { type: 'Features', variant: 'Timeline' },
    { type: 'Testimonials', variant: 'Marquee' },
    { type: 'Classes', variant: 'Default' },
    { type: 'AuthorizingBody', variant: 'Default' },
    { type: 'CTA', variant: 'GradientWave' },
  ],
};

// Theme homepage previews captured from school-sites /preview?theme=<name>.
export function themePreviewImage(theme) {
  try {
    return require(`@/assets/images/sectionLayouts/theme_${String(theme).toLowerCase()}.png`);
  } catch (e) {
    return require('@/assets/images/sectionLayouts/theme_default.png');
  }
}

// Section types with no captured thumbnails of their own reuse another type's
// gallery (AuthorizingBody is visually identical to Partners).
const SECTION_THUMB_ALIAS = {
  authorizingbody: 'partners',
};

// Thumbnails captured from the school-sites /preview gallery (one per section variant).
export function sectionLayoutImage(type, variant) {
  const key = String(type).toLowerCase();
  const thumbType = SECTION_THUMB_ALIAS[key] || key;
  try {
    return require(`@/assets/images/sectionLayouts/${thumbType}_${String(variant).toLowerCase()}.png`);
  } catch (e) {
    return require(`@/assets/images/sectionLayouts/${thumbType}_default.png`);
  }
}
